import qsTruthy from "./utils/qs_truthy";
import nextTick from "./utils/next-tick";
import { hackyMobileSafariTest } from "./utils/detect-touchscreen";
import { SignInMessages } from "./react-components/auth/SignInModal";
import { createNetworkedEntity } from "./utils/create-networked-entity";

const isBotMode = qsTruthy("bot");
const isMobile = AFRAME.utils.device.isMobile();
const forceEnableTouchscreen = hackyMobileSafariTest();
const isMobileVR = AFRAME.utils.device.isMobileVR();
const isDebug = qsTruthy("debug");
const qs = new URLSearchParams(location.search);

import { addMedia, coerceToUrl, getDefaultResolveQuality } from "./utils/media-utils";
import { isIn2DInterstitial, exit2DInterstitialAndEnterVR, forceExitFrom2DInterstitial } from "./utils/vr-interstitial";
import { ObjectContentOrigins } from "./object-types";
import { getAvatarType, resolveAvatarSrcWithFallback } from "./utils/avatar-utils";
import { buildAvatarSpawnPayload, isAvatarEntry } from "./utils/avatar-media-utils";
import {
  acquireWorkspaceMediaForSpawn,
  getWorkspaceMediaAcquisitionErrorMessage
} from "./utils/workspace-media-acquisition-utils";
import { handleStorageLimitError } from "./utils/storage-limit-utils";
import { emitLegacyPasteDropMediaEvent } from "./utils/legacy-url-placement-utils";
import { isParticipantLayoutActiveForGlobalMedia } from "./utils/participant-layout-active-utils";
import { SOUND_ENTER_SCENE } from "./systems/sound-effects-system";
import { MediaDevices, MediaDevicesEvents } from "./utils/media-devices-utils";
import { addComponent, removeEntity } from "bitecs";
import { MyCameraTool } from "./bit-components";
import { anyEntityWith } from "./utils/bit-utils";
import { moveToSpawnPoint } from "./bit-systems/waypoint";
import { spawnFromFileList, spawnFromUrl } from "./load-media-on-paste-or-drop";
import { isLockedDownDemoRoom } from "./utils/hub-utils";
import configManager from "./singleton/ConfigManager";
import configManagerEmitter from "./utils/configManagerEmitter";
import configs from "./utils/configs";
import { getLocalVideoSharingSources } from "./utils/local-video-share-utils";
import {
  removeLocalNetworkedAvatar,
  removeRemoteAvatarEntities,
  shouldSpawnNetworkedAvatar,
  shouldSuppressAvatarNetworking
} from "./utils/invisible-avatar-networking";
import { setInvisibleAvatarVoiceSinksEnabled } from "./utils/invisible-avatar-voice-sinks";

const useNewLoader = qsTruthy("newLoader");

function attemptExitCleanup(label, cleanup) {
  try {
    cleanup();
  } catch (error) {
    console.error(`Exit scene ${label} error:`, error);
  }
}

function isAcquirableHttpUrl(value) {
  if (typeof value !== "string") return false;

  try {
    const url = new URL(coerceToUrl(value) || value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}

export default class SceneEntryManager {
  constructor(hubChannel, authChannel, history) {
    this.hubChannel = hubChannel;
    this.authChannel = authChannel;
    this.store = window.APP.store;
    this.mediaSearchStore = window.APP.mediaSearchStore;
    this.scene = document.querySelector("a-scene");
    this.rightCursorController = document.getElementById("right-cursor-controller");
    this.leftCursorController = document.getElementById("left-cursor-controller");
    this.avatarRig = document.getElementById("avatar-rig");
    this._entered = false;
    this.performConditionalSignIn = () => {};
    this.history = history;
  }

  init = () => {
    configManagerEmitter.on("configUpdated", this._handleConfigUpdated);
    this._handleConfigUpdated(configManager.config);

    this.whenSceneLoaded(() => {
      this.rightCursorController.components["cursor-controller"].enabled = false;
      this.leftCursorController.components["cursor-controller"].enabled = false;
      this.mediaDevicesManager = APP.mediaDevicesManager;
      this._setupBlocking();
    });
  };

  hasEntered = () => {
    return this._entered;
  };

  _handleConfigUpdated = config => {
    if (!shouldSuppressAvatarNetworking(config)) {
      setInvisibleAvatarVoiceSinksEnabled(false);
      return;
    }

    removeLocalNetworkedAvatar(this.avatarRig);
    removeRemoteAvatarEntities();
    setInvisibleAvatarVoiceSinksEnabled(true);
  };

  enterInVR(enterInWebXrAR = false) {
    this.scene.addState("vr-entered");

    // HACK - A-Frame calls getVRDisplays at module load, we want to do it here to
    // force gamepads to become live.
    "getVRDisplays" in navigator && navigator.getVRDisplays();

    exit2DInterstitialAndEnterVR(true, enterInWebXrAR);
  }

  enterScene = async (enterInVR, muteOnEntry, enterInWebXrAR = false) => {
    document.getElementById("viewing-camera").removeAttribute("scene-preview-camera");

    if (isDebug && NAF.connection.adapter.session) {
      NAF.connection.adapter.session.options.verbose = true;
    }

    enterInVR && this.enterInVR(enterInWebXrAR);

    if (useNewLoader) {
      moveToSpawnPoint(APP.world, this.scene.systems["hubs-systems"].characterController);
    } else {
      const waypointSystem = this.scene.systems["hubs-systems"].waypointSystem;
      waypointSystem.moveToSpawnPoint();
    }

    if (
      (isMobile || forceEnableTouchscreen || qsTruthy("force_enable_touchscreen")) &&
      configManager.config.joystick.enabled
    ) {
      this.avatarRig.setAttribute("virtual-gamepad-controls", {});
    }

    if (configManager.config.avatarNetworking.enabled) {
      this._setupPlayerRig();
      this._setupKicking();
      this._setupMedia();
      this._setupCamera();

      if (shouldSpawnNetworkedAvatar(configManager.config)) {
        this._spawnAvatar();
      }

      this.scene.systems["hubs-systems"].soundEffectsSystem.playSoundOneShot(SOUND_ENTER_SCENE);
    }

    if (isBotMode) {
      this._runBot();
      this.scene.addState("entered");
      this.hubChannel.sendEnteredEvent();
      return;
    }

    this.scene.classList.remove("hand-cursor");
    this.scene.classList.add("no-cursor");

    // this.rightCursorController.components["cursor-controller"].enabled = true;
    // this.leftCursorController.components["cursor-controller"].enabled = true;
    this._entered = true;

    // Delay sending entry event telemetry until VR display is presenting.
    (async () => {
      while (enterInVR && !this.scene.renderer.xr.isPresenting) {
        await nextTick();
      }

      this.hubChannel.sendEnteredEvent().then(() => {
        this.store.update({ activity: { lastEnteredAt: new Date().toISOString() } });
      });
    })();

    // Bump stored entry count after 30s
    setTimeout(() => this.store.bumpEntryCount(), 30000);

    this.scene.addState("entered");

    if (muteOnEntry) {
      APP.mediaDevicesManager.micEnabled = false;
    } else {
      APP.mediaDevicesManager.enableMicOnEntry();
    }
  };

  whenSceneLoaded = callback => {
    if (this.scene.hasLoaded) {
      callback();
    } else {
      this.scene.addEventListener("loaded", callback);
    }
  };

  enterSceneWhenLoaded = (enterInVR, muteOnEntry) => {
    this.whenSceneLoaded(() => this.enterScene(enterInVR, muteOnEntry));
  };

  exitScene = () => {
    attemptExitCleanup("room navigation cancellation", () => {
      APP.roomTransition?.sourceTransition?.controller.abort();
      APP.roomTransition?.controller.abort();
      APP.roomTransition = null;
    });
    attemptExitCleanup("dialog disconnect", () => {
      if (APP.dialog && APP.dialog.disconnect) {
        APP.dialog.disconnect();
      }
    });

    attemptExitCleanup("local media cleanup", () => {
      if (APP.dialog && APP.dialog.localMediaStream) {
        APP.dialog.localMediaStream.getTracks().forEach(t => t.stop());
      }
    });

    attemptExitCleanup("NAF disconnect", () => {
      // Keep this before hub socket teardown so the Phoenix adapter can unregister its channel listeners.
      if (
        typeof NAF !== "undefined" &&
        NAF.connection &&
        NAF.connection.adapter &&
        typeof NAF.connection.disconnect === "function"
      ) {
        NAF.connection.disconnect();
      }
    });

    attemptExitCleanup("hub disconnect", () => {
      if (this.hubChannel) {
        this.hubChannel.disconnect();
      }
    });

    attemptExitCleanup("config cleanup", () => {
      configManagerEmitter.off("configUpdated", this._handleConfigUpdated);
    });

    attemptExitCleanup("VR exit", () => {
      this.scene.exitVR();
    });

    attemptExitCleanup("scene removal", () => {
      if (this.scene.renderer) {
        this.scene.renderer.setAnimationLoop(null); // Stop animation loop, TODO A-Frame should do this
      }
      if (this.scene.parentNode) {
        this.scene.parentNode.removeChild(this.scene);
      }
    });
  };

  _setupPlayerRig = () => {
    // Explict user action changed avatar or updated existing avatar.
    this.scene.addEventListener("avatar_updated", () => this._setPlayerInfoFromProfile(true));

    // Store updates can occur to avatar id in cases like error, auth reset, etc.
    if (!isLockedDownDemoRoom()) {
      this._setPlayerInfoFromProfile();
      this.store.addEventListener("statechanged", () => this._setPlayerInfoFromProfile());
    }

    const avatarScale = parseInt(qs.get("avatar_scale"), 10);
    if (avatarScale) {
      this.avatarRig.setAttribute("scale", { x: avatarScale, y: avatarScale, z: avatarScale });
    }
  };

  _setPlayerInfoFromProfile = async (force = false) => {
    const avatarId = this.store.state.profile.avatarId;
    if (!force && this._lastFetchedAvatarId === avatarId) return; // Avoid continually refetching based upon state changing

    this._lastFetchedAvatarId = avatarId;
    const resolvedAvatar = await resolveAvatarSrcWithFallback(avatarId, { store: this.store });

    this.avatarRig.setAttribute("player-info", {
      avatarSrc: resolvedAvatar.avatarSrc,
      avatarType: getAvatarType(resolvedAvatar.avatarId)
    });
  };

  _setupKicking = () => {
    // This event is only received by the kicker
    document.body.addEventListener("kicked", ({ detail }) => {
      const { clientId: kickedClientId } = detail;
      const { entities } = NAF.connection.entities;
      for (const id in entities) {
        const entity = entities[id];
        if (NAF.utils.getCreator(entity) !== kickedClientId) continue;

        if (entity.components.networked.data.persistent) {
          NAF.utils.takeOwnership(entity);
          window.APP.pinningHelper.unpinElement(entity);
          entity.parentNode.removeChild(entity);
        } else {
          NAF.entities.removeEntity(id);
        }
      }
    });
  };

  _setupBlocking = () => {
    document.body.addEventListener("blocked", ev => {
      NAF.connection.entities.removeEntitiesOfClient(ev.detail.clientId);
    });

    document.body.addEventListener("unblocked", ev => {
      NAF.connection.entities.completeSync(ev.detail.clientId, true);
    });
  };

  _setupMedia = () => {
    // HACK we only care about the return value in 1 spot, don't want to deal with that in the newLoader path
    const spawnMediaInFrontOfPlayerAndReturn = (
      src,
      contentOrigin,
      mediaOptions,
      fileIsOwned = false,
      index = null,
      assetSrc = null
    ) => {
      if (!this.hubChannel.can("spawn_and_move_media")) return;

      // Define base parameters
      const baseDistance = 1.5; // Distance in front of the player
      const spreadRadius = 1.0; // Radius for distribution
      let dynamicOffset = { x: 0, y: 0, z: -baseDistance };

      if (index !== null) {
        const angle = Math.random() * 2 * Math.PI;
        const radius = Math.random() * spreadRadius;
        dynamicOffset = {
          x: radius * Math.cos(angle),
          y: 0, // Keep y constant or adjust as needed
          z: -baseDistance + radius * Math.sin(angle)
        };
      }

      const { entity, orientation } = addMedia(
        src,
        "#interactable-media",
        contentOrigin,
        typeof src === "object" && src !== null && "name" in src && src.name === "message.png" ? "message" : null,
        !(src instanceof MediaStream),
        true,
        true,
        mediaOptions,
        true,
        null,
        null,
        fileIsOwned,
        assetSrc
      );

      orientation.then(or => {
        entity.setAttribute("offset-relative-to", {
          target: "#avatar-pov-node",
          offset: dynamicOffset,
          orientation: or
        });
      });

      return entity;
    };

    const spawnMediaInFrontOfPlayer = (
      src,
      contentOrigin,
      mediaOptions = {},
      fileIsOwned = false,
      index = null,
      assetSrc = null
    ) => {
      if (useNewLoader) {
        console.warn(
          "Spawning newLoader object using `spawnMediaInFrontOfPlayer`. This codepath should likely be made more direct.",
          src,
          contentOrigin
        );
        if (typeof src === "string") {
          spawnFromUrl(src);
        } else {
          spawnFromFileList([src]);
        }
      } else {
        spawnMediaInFrontOfPlayerAndReturn(src, contentOrigin, mediaOptions, fileIsOwned, index, assetSrc)?.eid;
      }
    };

    this.scene.addEventListener("add_media", async e => {
      const contentOrigin = e.detail.src instanceof File ? ObjectContentOrigins.FILE : ObjectContentOrigins.URL;
      const { src, href = "", hideHref = false, smoothTransition = false, assetSrc = null } = e.detail;
      const mediaOptions = {};
      const fileIsOwned = e.detail.fileIsOwned;

      if (href) {
        mediaOptions.href = href;
        mediaOptions.hideHref = hideHref;
        mediaOptions.smoothTransition = smoothTransition;
      }

      if (isAcquirableHttpUrl(src) && !fileIsOwned && !assetSrc) {
        try {
          this.scene.emit("media_acquisition_started", { src });
          const normalizedSrc = coerceToUrl(src) || src;
          const acquired = await acquireWorkspaceMediaForSpawn(normalizedSrc, {
            quality: getDefaultResolveQuality(),
            version: 1
          });
          this.scene.emit("media_acquisition_finished", { src });

          if (acquired?.src) {
            spawnMediaInFrontOfPlayer(
              acquired.src,
              contentOrigin,
              { ...mediaOptions, ...(acquired.mediaOptions || {}) },
              true,
              null,
              acquired.assetSrc || null
            );
            return;
          }
        } catch (error) {
          console.error("Media URL acquisition failed", error);
          this.scene.emit("media_acquisition_finished", { src });
          if (handleStorageLimitError(error, this.scene)) {
            return;
          }
          this.scene.emit("media_acquisition_failed", {
            src,
            error,
            message: getWorkspaceMediaAcquisitionErrorMessage(error)
          });
          return;
        }
      }

      spawnMediaInFrontOfPlayer(src, contentOrigin, mediaOptions, fileIsOwned, null, assetSrc);
    });

    this.scene.addEventListener("object_spawned", e => {
      this.hubChannel.sendObjectSpawnedEvent(e.detail.objectType);
    });

    this.scene.addEventListener("action_kick_client", ({ detail: { clientId } }) => {
      this.performConditionalSignIn(
        () => this.hubChannel.can("kick_users"),
        async () => await window.APP.hubChannel.kick(clientId),
        SignInMessages.kickUser
      );
    });

    this.scene.addEventListener("action_mute_client", ({ detail: { clientId } }) => {
      this.performConditionalSignIn(
        () => this.hubChannel.can("mute_users"),
        () => window.APP.hubChannel.mute(clientId),
        SignInMessages.muteUser
      );
    });

    this.scene.addEventListener("action_vr_notice_closed", () => forceExitFrom2DInterstitial());

    if (!useNewLoader) {
      document.addEventListener("paste", e => {
        if (
          (e.target.matches("input, textarea") || e.target.contentEditable === "true") &&
          document.activeElement === e.target
        )
          return;

        if (isParticipantLayoutActiveForGlobalMedia()) return;

        // Never paste into scene if dialog is open
        // TODO: Fix the in-modal-or-overlay effect, since it remains active even when the modal is closed
        // const uiRoot = document.querySelector(".ui-root");
        // if (uiRoot && uiRoot.classList.contains("in-modal-or-overlay")) return;

        const url = e.clipboardData.getData("text");
        const files = e.clipboardData.files && e.clipboardData.files;

        emitLegacyPasteDropMediaEvent(this.scene, { url, files });
      });

      let lastDebugScene;
      document.addEventListener("drop", e => {
        e.preventDefault();

        if (isParticipantLayoutActiveForGlobalMedia()) return;

        if (!this.hubChannel.can("spawn_and_move_media")) {
          window.dispatchEvent(new CustomEvent("not-permission-modal"));
          return;
        }

        // if a dropzone modal is open, don't handle the drop event
        if (document.getElementById("upload-object-modal")) {
          return;
        }

        // If a droparea is open, don't handle the drop event
        if (document.getElementById("droparea")) {
          return;
        }

        if (qsTruthy("debugLocalScene")) {
          URL.revokeObjectURL(lastDebugScene);
          const url = URL.createObjectURL(e.dataTransfer.files[0]);
          this.hubChannel.updateScene(url);
          lastDebugScene = url;
          return;
        }

        let url = e.dataTransfer.getData("url");

        if (!url) {
          // Sometimes dataTransfer text contains a valid URL, so try for that.
          try {
            url = new URL(e.dataTransfer.getData("text")).href;
          } catch (e) {
            // Nope, not this time.
          }
        }

        const files = e.dataTransfer.files;

        emitLegacyPasteDropMediaEvent(this.scene, { url, files });
      });
    }

    document.addEventListener("dragover", e => e.preventDefault());

    const currentVideoShareEntities = {};
    let isHandlingVideoShare = false;

    const shareSuccess = (isDisplayMedia, isVideoTrackAdded, target) => {
      isHandlingVideoShare = false;

      if (isVideoTrackAdded) {
        const source = isDisplayMedia ? MediaDevices.SCREEN : MediaDevices.CAMERA;

        if (target === "avatar") {
          this.avatarRig.setAttribute("player-info", { isSharingAvatarCamera: true });
        } else {
          const currentVideoShareEntity = spawnMediaInFrontOfPlayerAndReturn(
            this.mediaDevicesManager.mediaStream,
            undefined,
            { videoSource: source }
          );

          if (!currentVideoShareEntity) {
            return;
          }

          currentVideoShareEntities[source] = currentVideoShareEntity;
          // Wire up custom removal event which will stop the stream.
          currentVideoShareEntity.setAttribute(
            "emit-scene-event-on-remove",
            `event:${MediaDevicesEvents.VIDEO_SHARE_ENDED}; source:${source}`
          );

          // Set up a new interval to send data every second (or any other desired frequency)
          // shareInterval = setInterval(() => {
          //   let streamData = currentVideoShareEntity?.components["networked"]?.data;
          //   window.APP.syncNAFHelper.emitSyncStream(streamData);
          // }, 5000);

          setTimeout(() => {
            const streamData = currentVideoShareEntities[source]?.components["networked"]?.data;
            if (streamData?.networkId) {
              window.APP.syncNAFHelper.setNetworkId(streamData.networkId);
            }
          }, 300);
        }

        this.scene.emit("share_video_enabled", { source });
        this.scene.addState("sharing_video");
      }
    };

    const shareError = (error, source) => {
      console.error(error);
      isHandlingVideoShare = false;
      this.scene.emit("share_video_failed", { source });
    };

    this.scene.addEventListener("action_share_camera", event => {
      if (isHandlingVideoShare) return;
      if (!this.mediaDevicesManager.canStartVideoShare({ isDisplayMedia: false, notify: true })) {
        this.scene.emit("share_video_failed", { source: MediaDevices.CAMERA });
        return;
      }

      isHandlingVideoShare = true;
      this.mediaDevicesManager.startVideoShare({
        isDisplayMedia: false,
        target: event.detail?.target,
        success: shareSuccess,
        error: shareError
      });
    });

    this.scene.addEventListener("action_share_screen", () => {
      if (isHandlingVideoShare) return;
      if (!this.mediaDevicesManager.canStartVideoShare({ isDisplayMedia: true, notify: true })) {
        this.scene.emit("share_video_failed", { source: MediaDevices.SCREEN });
        return;
      }

      isHandlingVideoShare = true;
      this.mediaDevicesManager.startVideoShare({
        isDisplayMedia: true,
        target: null,
        success: shareSuccess,
        error: shareError
      });
    });

    this.scene.addEventListener(MediaDevicesEvents.VIDEO_SHARE_ENDED, async event => {
      if (isHandlingVideoShare) return;
      isHandlingVideoShare = true;
      const source = event.detail?.source || null;
      const sourcesToStop = source ? [source] : Object.keys(currentVideoShareEntities);

      for (const sourceToStop of sourcesToStop) {
        const currentVideoShareEntity = currentVideoShareEntities[sourceToStop];

        if (currentVideoShareEntity && currentVideoShareEntity.parentNode) {
          NAF.utils.takeOwnership(currentVideoShareEntity);
          currentVideoShareEntity.parentNode.removeChild(currentVideoShareEntity);
        }

        delete currentVideoShareEntities[sourceToStop];
      }

      await this.mediaDevicesManager.stopVideoShare({ source });

      if (!source || source === MediaDevices.CAMERA) {
        this.avatarRig.setAttribute("player-info", { isSharingAvatarCamera: false });
      }

      this.scene.emit("share_video_disabled", { source });

      if (getLocalVideoSharingSources(this.mediaDevicesManager).length === 0) {
        this.scene.removeState("sharing_video");
      }
      isHandlingVideoShare = false;
    });

    this.scene.addEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, async () => {
      await this.mediaDevicesManager.stopMicShare();
    });

    this.scene.addEventListener("action_selected_media_result_entry", async e => {
      // TODO spawn in space when no rights
      const { entry, selectAction } = e.detail;

      if (selectAction === "my-media") {
        window.dispatchEvent(new CustomEvent("open-media-modal", { detail: entry }));
        return;
      }
      if (selectAction !== "spawn") return;

      const delaySpawn = isIn2DInterstitial() && !isMobileVR;
      await exit2DInterstitialAndEnterVR();
      const spawnPayload = isAvatarEntry(entry)
        ? buildAvatarSpawnPayload(entry, { persistenceApiUrl: configs.PERSISTENCE_API_URL })
        : {
            src: entry.url,
            mediaOptions: entry.mediaOptions || null
          };
      const spawnSrc = spawnPayload.src;
      const spawnMediaOptions = spawnPayload.mediaOptions;

      if (!spawnSrc) return;

      // NGA has limited CORS support, so we need to fetch the media first and then pass it to spawnMediaInFrontOfPlayer
      if (spawnSrc.includes("api.nga")) {
        try {
          const response = await fetch(spawnSrc);
          if (!response.ok) {
            throw new Error(`Failed to fetch NGA media: ${response.statusText}`);
          }

          const blob = await response.blob();

          // Extract filename from URL or assign a default name
          const urlParts = spawnSrc.split("/");
          const filename = urlParts[urlParts.length - 1] || "nga_media";

          // Create a File from the blob
          const file = new File([blob], filename, { type: blob.type });

          // Spawn media with fileIsOwned set to true to prevent re-uploading
          spawnMediaInFrontOfPlayer(file, ObjectContentOrigins.URL, {}, true, entry.index);
        } catch (error) {
          // Optionally, handle the error by notifying the user or taking alternative actions
        }
        return;
      }

      // If user has HMD lifted up or gone through interstitial, delay spawning for now. eventually show a modal
      if (delaySpawn) {
        setTimeout(() => {
          spawnMediaInFrontOfPlayer(
            spawnSrc,
            ObjectContentOrigins.URL,
            spawnMediaOptions,
            false,
            entry.index,
            entry.assetSrc || null
          );
        }, 3000);
      } else {
        spawnMediaInFrontOfPlayer(
          spawnSrc,
          ObjectContentOrigins.URL,
          spawnMediaOptions,
          false,
          entry.index,
          entry.assetSrc || null
        );
      }
    });

    this.mediaSearchStore.addEventListener("media-exit", () => {
      exit2DInterstitialAndEnterVR();
    });
  };

  _setupCamera = () => {
    this.scene.addEventListener("action_toggle_camera", () => {
      const myCam = anyEntityWith(APP.world, MyCameraTool);
      if (myCam) {
        removeEntity(APP.world, myCam);
        this.scene.removeState("camera");
      } else {
        const avatarPov = document.querySelector("#avatar-pov-node").object3D;
        const eid = createNetworkedEntity(APP.world, "camera");
        addComponent(APP.world, MyCameraTool, eid);

        const obj = APP.world.eid2obj.get(eid);
        obj.position.copy(avatarPov.localToWorld(new THREE.Vector3(0, 0, -1.5)));
        obj.lookAt(avatarPov.getWorldPosition(new THREE.Vector3()));

        this.scene.addState("camera");
      }
    });
  };

  _spawnAvatar = () => {
    this.avatarRig.setAttribute("networked", "template: #remote-avatar; attachTemplateToLocal: false;");
    this.avatarRig.setAttribute("networked-avatar", "");
    this.avatarRig.emit("entered");
  };

  _runBot = async () => {
    const audioEl = document.createElement("audio");
    let audioInput;
    let dataInput;

    // Wait for startup to render form
    do {
      audioInput = document.querySelector("#bot-audio-input");
      dataInput = document.querySelector("#bot-data-input");
      await nextTick();
    } while (!audioInput || !dataInput);

    const getAudio = () => {
      audioEl.loop = true;
      audioEl.muted = false;
      audioEl.crossorigin = "anonymous";
      audioEl.src = URL.createObjectURL(audioInput.files[0]);
      document.body.appendChild(audioEl);
    };

    if (audioInput.files && audioInput.files.length > 0) {
      getAudio();
    } else {
      audioInput.onchange = getAudio;
    }

    const camera = document.querySelector("#avatar-pov-node");
    const leftController = document.querySelector("#player-left-controller");
    const rightController = document.querySelector("#player-right-controller");
    const getRecording = () => {
      fetch(URL.createObjectURL(dataInput.files[0]))
        .then(resp => resp.json())
        .then(recording => {
          camera.setAttribute("replay", "");
          camera.components["replay"].poses = recording.camera.poses;

          leftController.setAttribute("replay", "");
          leftController.components["replay"].poses = recording.left.poses;
          leftController.removeAttribute("visibility-by-path");
          leftController.removeAttribute("track-pose");
          leftController.setAttribute("visible", true);

          rightController.setAttribute("replay", "");
          rightController.components["replay"].poses = recording.right.poses;
          rightController.removeAttribute("visibility-by-path");
          rightController.removeAttribute("track-pose");
          rightController.setAttribute("visible", true);
        });
    };

    if (dataInput.files && dataInput.files.length > 0) {
      getRecording();
    } else {
      dataInput.onchange = getRecording;
    }

    await new Promise(resolve => audioEl.addEventListener("canplay", resolve));

    const audioStream = audioEl.captureStream
      ? audioEl.captureStream()
      : audioEl.mozCaptureStream
      ? audioEl.mozCaptureStream()
      : null;

    if (audioStream) {
      let audioVolume = Number(qs.get("audio_volume") || "1.0");
      if (isNaN(audioVolume)) {
        audioVolume = 1.0;
      }
      const audioContext = THREE.AudioContext.getContext();
      const audioSource = audioContext.createMediaStreamSource(audioStream);
      const audioDestination = audioContext.createMediaStreamDestination();
      const gainNode = audioContext.createGain();
      audioSource.connect(gainNode);
      gainNode.connect(audioDestination);
      gainNode.gain.value = audioVolume;

      const audioSystem = AFRAME.scenes[0].systems["hubs-systems"].audioSystem;
      audioSystem.addStreamToOutboundAudio("microphone", audioDestination.stream);
    }

    const connect = async () => {
      await APP.dialog.setLocalMediaStream(this.mediaDevicesManager.mediaStream);
      audioEl.play();
    };
    if (APP.dialog._sendTransport) {
      connect();
    } else {
      this.scene.addEventListener("didConnectToDialog", connect);
    }
  };
}
